"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import { ShoppingBag, ChevronLeft, ArrowLeft } from "lucide-react";
import { BookingStep } from "@/types/booking";
import { useState } from "react";
import CartDialog from "./CartDialog";
import { useImageLoader } from "@/hooks/useImageLoader";

export default function BookingHeader() {
  const {
    businessInfo,
    selectedServices,
    selectedPackages,
    giftCardCart,
    step,
    voucherToBeRedeemed,
    handleNextStep,
  } = useBooking();

  const [isCartOpen, setIsCartOpen] = useState(false);
  const logoValid = useImageLoader(businessInfo?.logoUrl);
  const cartCount =
    selectedServices.length + selectedPackages.length + (giftCardCart ? 1 : 0);

  const handleBack = () => {
    if (!businessInfo?.allowServiceStaffSelection) {
      handleNextStep(BookingStep.INITIAL);
      return;
    }
    if (step === 6) {
      handleNextStep(BookingStep.INITIAL);
      return;
    }
    if (step > BookingStep.INITIAL) {
      handleNextStep(step - 1);
    }
  };

  const headerText = () => {
    if (step === 1) {
      return "Select staff";
    }
    if (step === 2) {
      return "Select date and time";
    }
    if (step === 3) {
      return "Your details";
    }
    if (step === 4) {
      if (voucherToBeRedeemed) {
        return "Redeem voucher";
      }
      return "Payment";
    }
    if (step === 6) {
      return "Checkout";
    }
    return null;
  };

  const goHome = () => {
    if (step == 2) {
      if (businessInfo?.allowServiceStaffSelection) {
        handleNextStep(BookingStep.STAFF_SELECTION);
      } else {
        handleNextStep(BookingStep.INITIAL);
      }
      return;
    }
    handleNextStep(BookingStep.INITIAL);
  };

  return (
    <header className="w-full sticky top-0 z-50 bg-white border-b border-grey-100">
      <div className="flex items-center justify-between px-4 py-4 lg:px-20">
        {/* Logo and Business Name */}
        <div className="flex items-center gap-4">
          {" "}
          {headerText() && (
            <ArrowLeft className="h-4 w-4" onClick={() => handleBack()} />
          )}
          <div className="flex flex-col">
            {logoValid && headerText() == null ? (
              <img
                src={businessInfo!.logoUrl!}
                alt={businessInfo!.name}
                className="h-10 w-auto object-contain rounded-full"
              />
            ) : (
              <h1 className="text-xl font-semibold text-grey-900">
                {headerText() || businessInfo?.name}
              </h1>
            )}
          </div>
        </div>

        {/* Desktop Actions */}
        {step !== BookingStep.SUCCESS && step !== BookingStep.PAYMENT ? (
          <div className="hidden lg:flex items-center gap-4">
            {/* Cart Button */}
            <Button
              variant="ghost"
              className="relative"
              onClick={() => setIsCartOpen(true)}
            >
              <ShoppingBag className="h-5 w-5 mr-2" />
              My Cart ({cartCount})
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-green-300 text-white text-xs flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Back to Home */}
            {step === BookingStep.PURCHASE_SUCCESSFUL && (
              <Button variant="secondary" onClick={goHome}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            )}
          </div>
        ) : null}
        {/* Mobile Cart Button Only */}
        {step !== BookingStep.SUCCESS && step !== BookingStep.PAYMENT ? (
          <div className="flex lg:hidden items-center gap-2">
            {cartCount > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setIsCartOpen(true)}
              >
                <ShoppingBag className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-green-300 text-white text-xs flex items-center justify-center">
                  {cartCount}
                </span>
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {/* Cart Dialog */}
      <CartDialog open={isCartOpen} onOpenChange={setIsCartOpen} />
    </header>
  );
}
